import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UploadProgress } from './upload-progress';
import { DataTable } from './data-table';
import { createUploadColumns } from './upload-columns';
import autoUploadsAPI from '@/api/auto-uploads.api';
import { useToast } from '@/components/ui/use-toast';

export function UploadHistory({ organizationId, workspaces = [] }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const expandedUploadId = searchParams.get('uploadId');

  const fetchUploads = async () => {
    setLoading(true);
    try {
      const { data, error } = await autoUploadsAPI.getAllUploads({ limit: 100 });

      if (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to fetch uploads',
          variant: 'destructive',
        });
        return;
      }

      setUploads(data.uploads || []);
    } catch (error) {
      console.error('Error fetching uploads:', error);
      toast({
        title: 'Error',
        description: 'Failed to load upload history.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshTable = async () => {
    await fetchUploads();
  };

  useEffect(() => {
    fetchUploads();
  }, [organizationId]);

  const handleExpandedChange = uploadId => {
    const currentTab = searchParams.get('tab') || 'history';
    if (uploadId) {
      navigate(`?tab=${currentTab}&uploadId=${uploadId}`);
    } else {
      navigate(`?tab=${currentTab}`);
    }
  };

  const columns = createUploadColumns(workspaces);

  const renderExpandedRow = upload => {
    return <UploadProgress uploadId={upload._id} createdAt={upload.createdAt} />;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload History</CardTitle>
          <CardDescription>View and manage all bulk uploads for this organization</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={uploads}
            onRefresh={handleRefreshTable}
            loading={loading}
            workspaces={workspaces}
            renderExpandedRow={renderExpandedRow}
            expandedId={expandedUploadId}
            onExpandedChange={handleExpandedChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
